import { useState } from "react";
import { Columns3 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PageHeader } from "@/components/PageHeader";
import { ViewModeToggle } from "@/components/leads-pipeline/ViewModeToggle";
import { ManageOrderColumnsModal } from "@/components/orders/board/ManageOrderColumnsModal";
import { OrderBoardView } from "@/components/orders/board/OrderBoardView";
import { OrderFilters } from "@/components/orders/board/OrderFilters";
import { OrderTableView } from "@/components/orders/board/OrderTableView";
import { useOrderBoardStages } from "@/hooks/useOrderBoardStages";
import { useOrdersBoard, useOrdersTable } from "@/hooks/useOrders";
import { useCurrentUserProfile } from "@/hooks/useUsers";
import { buildOrderColumns } from "@/lib/order-board";
import { hasActiveOrderFilters, useOrdersStore } from "@/stores/orders.store";

/**
 * Pedidos — the shape of Pipeline de Prospectos, with the order's STATUS as the
 * column axis instead of the lead's stage.
 *
 * Board and table read one filter set from `orders.store`, so switching view
 * never loses what someone narrowed down to. No «Enviar mensaje», no
 * «Etiquetas» and no «+ Nuevo»: an order is something a conversation produces,
 * and there is no endpoint that creates one by hand.
 */
export function Pedidos(): React.JSX.Element {
  const viewMode = useOrdersStore((s) => s.viewMode);
  const setViewMode = useOrdersStore((s) => s.setViewMode);
  const filtersActive = useOrdersStore(hasActiveOrderFilters);
  const profile = useCurrentUserProfile();
  // Column management is admin-only — a column is board structure. Matches
  // `authorizeAdmin` on the server, as the Prospectos page does.
  const isAdmin =
    profile?.role === "admin" ||
    profile?.role === "super_admin" ||
    profile?.role === "account_manager";

  const isBoard = viewMode === "board";
  const { data: stages } = useOrderBoardStages();
  const columns = buildOrderColumns(stages ?? []);

  // Only the visible mode fetches — the two share every filter, so otherwise
  // each keystroke in the search box would fire two requests.
  const board = useOrdersBoard({ enabled: isBoard });
  const table = useOrdersTable({ enabled: !isBoard });

  const [manageOpen, setManageOpen] = useState(false);

  // Real per-column totals from the server: the board only loads the top slice
  // of each column. «Columnas» uses them to block deleting a column that still
  // holds orders, which is why the button is disabled while a filter (which
  // shrinks them) is active.
  const counts = Object.fromEntries(
    (board.data?.counts ?? []).map((c) => [c.column, c.count]),
  );
  const count = isBoard
    ? Object.values(counts).reduce((n, c) => n + c, 0)
    : (table.data?.total ?? 0);
  const showCount = isBoard ? !board.isLoading : !table.isLoading;

  const manageColumnsButton = (
    className: string,
    content: React.ReactNode,
  ): React.JSX.Element => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <button
              type="button"
              onClick={() => setManageOpen(true)}
              disabled={filtersActive}
              aria-label="Administrar columnas"
              className={`${className} disabled:cursor-not-allowed disabled:opacity-50`}
            >
              {content}
            </button>
          </span>
        </TooltipTrigger>
        {filtersActive && (
          <TooltipContent side="bottom">
            <p>Limpia los filtros para administrar columnas</p>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );

  const content =
    isBoard && board.isError ? (
      <p className="px-4 py-12 text-center text-sm text-red-500 md:px-0 md:py-0 md:text-left">
        Error al cargar los pedidos. Intenta de nuevo.
      </p>
    ) : isBoard ? (
      <OrderBoardView
        columns={columns}
        items={board.data?.items ?? []}
        counts={counts}
        isLoading={board.isLoading}
      />
    ) : (
      <OrderTableView data={table.data} isLoading={table.isLoading} columns={columns} />
    );

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col md:h-full md:gap-4 md:p-6">
        {/* ── Mobile chrome ─────────────────────────── */}
        <div className="md:hidden">
          <PageHeader
            title="Pedidos"
            subtitle={showCount ? `${count} resultados` : undefined}
            right={
              isAdmin && isBoard
                ? manageColumnsButton(
                    "flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-ink-200 text-ink-700",
                    <Columns3 size={15} />,
                  )
                : undefined
            }
          />
          <div className="shrink-0 border-b border-ink-100 bg-white px-4 py-2">
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>
        </div>

        {/* ── Desktop chrome ────────────────────────── */}
        <div className="hidden items-center justify-between md:flex">
          <div className="flex items-center gap-3">
            <h1 className="font-display text-2xl font-bold text-ink-900">Pedidos</h1>
            {showCount && (
              <Badge variant="secondary" className="text-xs">
                {count}
              </Badge>
            )}
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
          </div>
          {isAdmin &&
            isBoard &&
            manageColumnsButton(
              "cursor-pointer rounded-md border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-050",
              "Columnas",
            )}
        </div>

        <OrderFilters columns={columns} />

        {content}
      </div>

      {isAdmin && (
        <ManageOrderColumnsModal
          open={manageOpen}
          onClose={() => setManageOpen(false)}
          stages={stages ?? []}
          orderCounts={counts}
        />
      )}
    </>
  );
}
